import { useState, useRef, useCallback, useEffect, RefObject, MouseEvent, WheelEvent } from 'react';

interface UseZoomPanOptions {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
}

interface Position {
  x: number;
  y: number;
}

interface UseZoomPanReturn {
  zoom: number;
  position: Position;
  isDragging: boolean;
  isPanMode: boolean;
  containerRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  handlers: {
    onMouseDown: (e: MouseEvent) => void;
    onMouseMove: (e: MouseEvent) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
    onWheel: (e: WheelEvent) => void;
  };
}

export function useZoomPan(options: UseZoomPanOptions = {}): UseZoomPanReturn {
  const { minZoom = 0.5, maxZoom = 3, zoomStep = 0.25 } = options;

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isPanMode, setIsPanMode] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const positionStartRef = useRef<Position>({ x: 0, y: 0 });

  // Constrain position to keep content within bounds
  const constrainPosition = useCallback((pos: Position, currentZoom: number): Position => {
    if (currentZoom <= 1) {
      return { x: 0, y: 0 };
    }

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return pos;

    const containerRect = container.getBoundingClientRect();
    const contentWidth = content.scrollWidth * currentZoom;
    const contentHeight = content.scrollHeight * currentZoom;

    // Calculate the maximum allowed pan distance
    const maxPanX = Math.max(0, (contentWidth - containerRect.width) / 2);
    const maxPanY = Math.max(0, (contentHeight - containerRect.height) / 2);

    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, pos.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, pos.y)),
    };
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => {
      const newZoom = Math.min(maxZoom, z + zoomStep);
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, [maxZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const newZoom = Math.max(minZoom, z - zoomStep);
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      } else {
        setPosition((pos) => constrainPosition(pos, newZoom));
      }
      return newZoom;
    });
  }, [minZoom, zoomStep, constrainPosition]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Reset position when zoom returns to 1
  useEffect(() => {
    if (zoom <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [zoom]);

  // Track Alt key state for pan mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') setIsPanMode(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') setIsPanMode(false);
    };
    const handleBlur = () => {
      // Reset pan mode when window loses focus
      setIsPanMode(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (zoom <= 1 || !isPanMode) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { ...position };
  }, [zoom, position, isPanMode]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    const newPosition = {
      x: positionStartRef.current.x + deltaX,
      y: positionStartRef.current.y + deltaY,
    };

    setPosition(constrainPosition(newPosition, zoom));
  }, [isDragging, zoom, constrainPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    // Only zoom on Ctrl+scroll or pinch gesture
    if (!e.ctrlKey && !e.metaKey) return;

    e.preventDefault();

    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    setZoom((z) => {
      const newZoom = Math.max(minZoom, Math.min(maxZoom, z + delta));
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      } else {
        setPosition((pos) => constrainPosition(pos, newZoom));
      }
      return newZoom;
    });
  }, [minZoom, maxZoom, zoomStep, constrainPosition]);

  return {
    zoom,
    position,
    isDragging,
    isPanMode,
    containerRef: containerRef as RefObject<HTMLDivElement>,
    contentRef: contentRef as RefObject<HTMLDivElement>,
    zoomIn,
    zoomOut,
    resetZoom,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onWheel: handleWheel,
    },
  };
}
